import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import {
  Users, DollarSign, TrendingUp, PieChart, FileText, GraduationCap,
  Calculator, CheckCircle2, Circle, Upload, AlertTriangle, Clock,
  Activity, ArrowUpRight, ArrowDownRight, Wallet, Landmark, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RPieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';

// ==================== Helpers ====================

const fmtAUD = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(v);

const fmtAUDk = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, notation: 'compact' }).format(v);

// ==================== Mock Data ====================

const FUND_BALANCE = 1_850_000;

const RECENT_ACTIVITY = [
  { date: '15/04/2026', description: 'Employer SG contribution received', amount: 2_968, type: 'credit' as const },
  { date: '10/04/2026', description: 'Quarterly pension payment - John', amount: -12_500, type: 'debit' as const },
  { date: '02/04/2026', description: 'Dividend income - Vanguard ETF', amount: 3_420, type: 'credit' as const },
  { date: '28/03/2026', description: 'Administration fee', amount: -400, type: 'debit' as const },
  { date: '15/03/2026', description: 'Employer SG contribution received', amount: 2_968, type: 'credit' as const },
];

const MEMBERS_OVERVIEW = [
  { name: 'John Smith', phase: 'Pension', balance: 948_000, age: 67 },
  { name: 'Jane Smith', phase: 'Accumulation', balance: 748_000, age: 58 },
];

// Contributions
const CONTRIBUTION_CAPS = {
  concessional: { used: 15_000, cap: 30_000 },
  nonConcessional: { used: 50_000, cap: 120_000 },
  unusedConcessional: 12_500,
};

// Pension
const PENSION_DATA = [
  { month: 'Jul', minimum: 4_167, paid: 4_167 },
  { month: 'Aug', minimum: 4_167, paid: 4_167 },
  { month: 'Sep', minimum: 4_167, paid: 4_167 },
  { month: 'Oct', minimum: 4_167, paid: 4_167 },
  { month: 'Nov', minimum: 4_167, paid: 4_167 },
  { month: 'Dec', minimum: 4_167, paid: 4_167 },
  { month: 'Jan', minimum: 4_167, paid: 4_167 },
  { month: 'Feb', minimum: 4_167, paid: 4_167 },
  { month: 'Mar', minimum: 4_167, paid: 4_167 },
  { month: 'Apr', minimum: 4_167, paid: 0 },
  { month: 'May', minimum: 4_167, paid: 0 },
  { month: 'Jun', minimum: 4_167, paid: 0 },
];

const PENSION_ANNUAL_MIN = 50_000;
const PENSION_YTD_PAID = 37_503;

// Investments
const ASSET_ALLOCATION = [
  { name: 'Australian Shares', value: 520_000, color: '#5048e5' },
  { name: 'International Shares', value: 180_000, color: '#06b6d4' },
  { name: 'Residential Property', value: 650_000, color: '#f59e0b' },
  { name: 'Term Deposits', value: 200_000, color: '#10b981' },
  { name: 'Managed Funds', value: 175_000, color: '#8b5cf6' },
  { name: 'Cash', value: 125_000, color: '#94a3b8' },
];

const HOLDINGS = [
  { name: 'Vanguard Australian Shares ETF (VAS)', units: 2_400, price: 98.50, value: 236_400 },
  { name: 'BHP Group Ltd', units: 800, price: 45.20, value: 36_160 },
  { name: 'CSL Limited', units: 350, price: 310.80, value: 108_780 },
  { name: 'Commonwealth Bank', units: 1_200, price: 115.55, value: 138_660 },
  { name: 'Vanguard International ETF (VGS)', units: 1_800, price: 100.00, value: 180_000 },
  { name: 'Macquarie Managed Fund', units: 8_750, price: 20.00, value: 175_000 },
];

const PERFORMANCE_DATA = [
  { month: 'May 25', value: 1_720_000 },
  { month: 'Jun 25', value: 1_695_000 },
  { month: 'Jul 25', value: 1_740_000 },
  { month: 'Aug 25', value: 1_758_000 },
  { month: 'Sep 25', value: 1_725_000 },
  { month: 'Oct 25', value: 1_780_000 },
  { month: 'Nov 25', value: 1_798_000 },
  { month: 'Dec 25', value: 1_810_000 },
  { month: 'Jan 26', value: 1_795_000 },
  { month: 'Feb 26', value: 1_820_000 },
  { month: 'Mar 26', value: 1_835_000 },
  { month: 'Apr 26', value: 1_850_000 },
];

// Documents
const DOCUMENT_CHECKLIST = [
  { label: 'Bank statements (all accounts) - FY2025/26', uploaded: true, dueDate: '15/08/2026' },
  { label: 'Property valuation report - 42 Ocean Drive', uploaded: false, dueDate: '31/07/2026' },
  { label: 'Share trade history / broker statements', uploaded: true, dueDate: '15/08/2026' },
  { label: 'Insurance certificate of currency', uploaded: false, dueDate: '31/07/2026' },
  { label: 'Employer SG contribution statements', uploaded: true, dueDate: '15/08/2026' },
  { label: 'Signed trustee declaration', uploaded: false, dueDate: '30/09/2026' },
];

// Education
const EDUCATION_MODULES = [
  {
    title: 'Your Responsibilities as Trustee',
    description: 'Understand your legal obligations under the SIS Act, including record keeping, investment decisions, and reporting requirements.',
    progress: 100,
  },
  {
    title: 'Investment Strategy Requirements',
    description: 'Learn about formulating, reviewing, and documenting your fund\'s investment strategy in line with s.52 SIS Act.',
    progress: 60,
  },
  {
    title: 'Contribution Rules',
    description: 'Contribution caps, bring-forward arrangements, work test requirements, and s290-170 deduction notices.',
    progress: 30,
  },
  {
    title: 'Pension Requirements',
    description: 'Minimum pension standards, commutation rules, TBAR reporting, and transfer balance cap obligations.',
    progress: 0,
  },
];

// ==================== Component ====================

export default function SMSFClientPortalPage() {
  const [tab, setTab] = useState('dashboard');

  // --- API Queries ---
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const firstFundId = fundsData?.[0]?.id;

  const { data: fundDetail, isLoading: fundLoading } = useQuery({
    queryKey: ['smsf-fund-detail', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  const { data: apiMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['smsf-fund-members', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/members`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  const { data: apiContributions, isLoading: contribLoading } = useQuery({
    queryKey: ['smsf-fund-contributions', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/contributions`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  // Use API data with mock fallback
  const members = apiMembers || MEMBERS_OVERVIEW;
  const fundBalance = fundDetail?.totalBalance ?? FUND_BALANCE;
  const isDataLoading = fundsLoading || fundLoading || membersLoading || contribLoading;

  // Retirement calculator state
  const [calcAge, setCalcAge] = useState(58);
  const [calcBalance, setCalcBalance] = useState(748_000);
  const [calcContrib, setCalcContrib] = useState(30_000);
  const [calcReturn, setCalcReturn] = useState(7);
  const [calcRetireAge, setCalcRetireAge] = useState(67);

  const projectionData = (() => {
    const data: { age: number; balance: number }[] = [];
    let bal = calcBalance;
    for (let age = calcAge; age <= Math.max(calcRetireAge, calcAge); age++) {
      data.push({ age, balance: Math.round(bal) });
      bal = bal * (1 + calcReturn / 100) + calcContrib;
    }
    return data;
  })();

  const projectedBalance = projectionData.length > 0 ? projectionData[projectionData.length - 1].balance : calcBalance;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title="SMSF Trustee Portal"
        description="Trustee-facing dashboard preview - what your SMSF clients see"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="contributions">My Contributions</TabsTrigger>
          <TabsTrigger value="pension">My Pension</TabsTrigger>
          <TabsTrigger value="investments">My Investments</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="education">Education</TabsTrigger>
          <TabsTrigger value="calculator">Retirement Calculator</TabsTrigger>
        </TabsList>

        {/* ===== Dashboard ===== */}
        <TabsContent value="dashboard" className="space-y-4">
          {isDataLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center">
                    <Wallet className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Fund Balance</p>
                    <p className="text-2xl font-bold">{fmtAUDk(fundBalance)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-500/10 text-green-600 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Compliance Status</p>
                    <div className="flex items-center gap-1.5">
                      <span className="h-3 w-3 rounded-full bg-green-500" />
                      <p className="text-lg font-semibold text-green-700">Compliant</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Members</p>
                    <p className="text-lg font-semibold">{members.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Member Cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            {members.map((m: any) => (
              <Card key={m.name}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-muted-foreground">Age {m.age}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={m.phase === 'Pension' ? 'default' : 'secondary'}>{m.phase}</Badge>
                      <p className="text-sm font-bold mt-1">{fmtAUD(m.balance)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {RECENT_ACTIVITY.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      {a.type === 'credit'
                        ? <ArrowUpRight className="h-4 w-4 text-green-600" />
                        : <ArrowDownRight className="h-4 w-4 text-red-500" />}
                      <div>
                        <p>{a.description}</p>
                        <p className="text-xs text-muted-foreground">{a.date}</p>
                      </div>
                    </div>
                    <span className={cn('font-mono font-medium', a.type === 'credit' ? 'text-green-700' : 'text-red-600')}>
                      {a.type === 'credit' ? '+' : ''}{fmtAUD(a.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== My Contributions ===== */}
        <TabsContent value="contributions" className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Concessional */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Concessional Contributions</CardTitle>
                <CardDescription>Employer SG, salary sacrifice, personal deductible</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between text-sm mb-1">
                  <span>Used: {fmtAUD(CONTRIBUTION_CAPS.concessional.used)}</span>
                  <span>Cap: {fmtAUD(CONTRIBUTION_CAPS.concessional.cap)}</span>
                </div>
                <Progress
                  value={(CONTRIBUTION_CAPS.concessional.used / CONTRIBUTION_CAPS.concessional.cap) * 100}
                  className="h-3 mb-2"
                />
                <p className="text-xs text-muted-foreground">
                  Remaining: {fmtAUD(CONTRIBUTION_CAPS.concessional.cap - CONTRIBUTION_CAPS.concessional.used)}
                </p>
              </CardContent>
            </Card>
            {/* Non-Concessional */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Non-Concessional Contributions</CardTitle>
                <CardDescription>After-tax personal contributions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between text-sm mb-1">
                  <span>Used: {fmtAUD(CONTRIBUTION_CAPS.nonConcessional.used)}</span>
                  <span>Cap: {fmtAUD(CONTRIBUTION_CAPS.nonConcessional.cap)}</span>
                </div>
                <Progress
                  value={(CONTRIBUTION_CAPS.nonConcessional.used / CONTRIBUTION_CAPS.nonConcessional.cap) * 100}
                  className="h-3 mb-2"
                />
                <p className="text-xs text-muted-foreground">
                  Remaining: {fmtAUD(CONTRIBUTION_CAPS.nonConcessional.cap - CONTRIBUTION_CAPS.nonConcessional.used)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Unused cap alert */}
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Unused Concessional Cap Available</p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    You have {fmtAUD(CONTRIBUTION_CAPS.unusedConcessional)} of unused concessional cap from prior years available under the carry-forward rules (total super balance under $500,000).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <Button variant="outline" className="gap-1.5" onClick={() => toast.info('Contribution calculator coming soon')}>
                <Calculator className="h-4 w-4" /> How much more can I contribute?
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== My Pension ===== */}
        <TabsContent value="pension" className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-xs text-muted-foreground">Annual Minimum</p>
                <p className="text-xl font-bold">{fmtAUD(PENSION_ANNUAL_MIN)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-xs text-muted-foreground">YTD Paid</p>
                <p className="text-xl font-bold text-green-700">{fmtAUD(PENSION_YTD_PAID)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-xs text-muted-foreground">Remaining</p>
                <p className="text-xl font-bold text-amber-600">{fmtAUD(PENSION_ANNUAL_MIN - PENSION_YTD_PAID)}</p>
                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-1">
                  <Clock className="h-3 w-3" /> Next payment: 10/05/2026
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Minimum Required vs Paid (Monthly)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={PENSION_DATA}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => fmtAUD(Number(value))} contentStyle={{ borderRadius: 8, fontSize: 13 }} cursor={false} />
                    <Bar dataKey="minimum" name="Minimum Required" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={20} activeBar={false}/>
                    <Bar dataKey="paid" name="Paid" fill="#5048e5" radius={[4, 4, 0, 0]} barSize={20} activeBar={false}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== My Investments ===== */}
        <TabsContent value="investments" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Donut Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="h-4 w-4" /> Asset Allocation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RPieChart>
                      <Pie
                        data={ASSET_ALLOCATION}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {ASSET_ALLOCATION.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => fmtAUD(Number(value))} contentStyle={{ borderRadius: 8, fontSize: 13 }} cursor={false} />
                    </RPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 justify-center mt-2">
                  {ASSET_ALLOCATION.map((a) => (
                    <span key={a.name} className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: a.color }} />
                      {a.name}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Performance Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" /> 12-Month Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={PERFORMANCE_DATA}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tickFormatter={(v: number) => `$${(v / 1_000_000).toFixed(1)}M`}
                        tick={{ fontSize: 11 }}
                        domain={['dataMin - 50000', 'dataMax + 50000']}
                      />
                      <Tooltip formatter={(value) => fmtAUD(Number(value))} contentStyle={{ borderRadius: 8, fontSize: 13 }} cursor={false} />
                      <Line type="monotone" dataKey="value" stroke="#5048e5" strokeWidth={2} dot={{ r: 3 }} name="Fund Value" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Holdings Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Holdings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Investment</th>
                      <th className="pb-2 pr-4 font-medium text-right">Units</th>
                      <th className="pb-2 pr-4 font-medium text-right">Price</th>
                      <th className="pb-2 font-medium text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {HOLDINGS.map((h) => (
                      <tr key={h.name} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 font-medium">{h.name}</td>
                        <td className="py-2.5 pr-4 text-right font-mono">{h.units.toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right font-mono">${h.price.toFixed(2)}</td>
                        <td className="py-2.5 text-right font-mono font-medium">{fmtAUD(h.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Documents ===== */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" /> Documents Required
              </CardTitle>
              <CardDescription>Your accountant needs the following documents. Please upload as soon as possible.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {DOCUMENT_CHECKLIST.map((doc) => (
                  <div key={doc.label} className="flex items-center gap-3 border rounded-lg p-3">
                    {doc.uploaded
                      ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                      : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm', doc.uploaded ? 'font-medium' : 'text-muted-foreground')}>{doc.label}</p>
                      <p className="text-xs text-muted-foreground">Due: {doc.dueDate}</p>
                    </div>
                    {doc.uploaded ? (
                      <Badge variant="default">Uploaded</Badge>
                    ) : (
                      <Button variant="outline" size="sm" className="gap-1 shrink-0" onClick={() => toast.info('File upload coming soon')}>
                        <Upload className="h-3.5 w-3.5" /> Upload
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Education ===== */}
        <TabsContent value="education" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <GraduationCap className="h-4 w-4" /> Trustee Education Modules
              </CardTitle>
              <CardDescription>Complete these modules to understand your obligations as an SMSF trustee</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {EDUCATION_MODULES.map((mod) => (
                <div key={mod.title} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium">{mod.title}</p>
                    <Badge variant={mod.progress === 100 ? 'default' : mod.progress > 0 ? 'secondary' : 'outline'}>
                      {mod.progress === 100 ? 'Completed' : mod.progress > 0 ? `${mod.progress}%` : 'Not Started'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{mod.description}</p>
                  <Progress value={mod.progress} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Retirement Calculator ===== */}
        <TabsContent value="calculator" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-4 w-4" /> Retirement Projection
                </CardTitle>
                <CardDescription>Estimate your super balance at retirement</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Current Age</label>
                  <Input type="number" value={calcAge} onChange={e => setCalcAge(Number(e.target.value))} min={18} max={75} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Current Super Balance ($)</label>
                  <Input type="number" value={calcBalance} onChange={e => setCalcBalance(Number(e.target.value))} min={0} step={10000} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Annual Contributions ($)</label>
                  <Input type="number" value={calcContrib} onChange={e => setCalcContrib(Number(e.target.value))} min={0} step={1000} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Expected Return (% p.a.)</label>
                  <Input type="number" value={calcReturn} onChange={e => setCalcReturn(Number(e.target.value))} min={0} max={20} step={0.5} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Retirement Age</label>
                  <Input type="number" value={calcRetireAge} onChange={e => setCalcRetireAge(Number(e.target.value))} min={calcAge} max={80} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Projected Balance at Age {calcRetireAge}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <p className="text-3xl font-bold text-[#5048e5]">{fmtAUD(projectedBalance)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Over {calcRetireAge - calcAge} years at {calcReturn}% p.a. return
                  </p>
                </div>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={projectionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="age" tick={{ fontSize: 11 }} label={{ value: 'Age', position: 'insideBottom', offset: -5 }} />
                      <YAxis
                        tickFormatter={(v: number) => `$${(v / 1_000_000).toFixed(1)}M`}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip formatter={(value) => fmtAUD(Number(value))} contentStyle={{ borderRadius: 8, fontSize: 13 }} cursor={false} />
                      <Line type="monotone" dataKey="balance" stroke="#5048e5" strokeWidth={2} dot={false} name="Projected Balance" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[10px] text-muted-foreground text-center mt-2">
                  This is a simplified projection only. Actual returns will vary. Does not account for tax, fees, or inflation.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
